"use client";

import { useMemo } from "react";
import useOrgSlug from "./use-org-slug";
import { buildOrgUrl, buildProjectUrl, buildAdminUrl } from "../utils/url.utils";

/**
 * Hook providing URL building helpers for the current organization
 * Uses the org slug from the URL to construct org-prefixed paths
 */
const useOrgUrl = () => {
  const orgSlug = useOrgSlug();

  return useMemo(
    () => ({
      orgSlug,
      /**
       * Build a path within the current org
       * @param path - The path within the org (e.g., /projects)
       */
      toOrgPath: (path: string) => buildOrgUrl(orgSlug, path),
      /**
       * Build a project path within the current org
       * @param projectId - The project ID
       * @param subPath - Optional sub-path within the project
       */
      toProjectPath: (projectId: string, subPath = "") =>
        buildProjectUrl(orgSlug, projectId, subPath),
      /**
       * Build an admin path within the current org
       * @param subPath - Optional sub-path within admin
       */
      toAdminPath: (subPath = "") => buildAdminUrl(orgSlug, subPath),
      /**
       * Get the projects list URL for the current org
       */
      projectsUrl: buildOrgUrl(orgSlug, "/projects"),
      /**
       * Get the admin URL for the current org
       */
      adminUrl: buildOrgUrl(orgSlug, "/admin"),
    }),
    [orgSlug]
  );
};

export default useOrgUrl;
